import { ExceptionEvent } from "@fairfleet/geotab";
import { GeotabCall } from "./fetchExceptionEvents";

/** Coordinates per `GetAddresses` call. */
const ADDRESS_BUNDLE = 100;

const idOf = (ref: unknown) => (typeof ref === "string" ? ref : (ref as { id?: string } | undefined)?.id);

/**
 * The zones at each visit's start, as the MyGeotab exception report fills „ExceptionDetailLocation“: the
 * position at `activeFrom` (a LogRecord Get with from = to returns one record interpolated to that moment,
 * live fairfleet 2026-11-04), then `GetAddresses`, which lists the zones there.
 * All position reads fire at once on purpose: the client bundles them into ExecuteMultiCalls of 100 and counts
 * every entry against the per-minute limit. A visit without a position is left out.
 */
export async function locateVisits(
  call: GeotabCall,
  events: ExceptionEvent[],
  { addressBundle = ADDRESS_BUNDLE, signal }: { addressBundle?: number; signal?: AbortSignal } = {},
): Promise<{ zones: Map<string, string[]>; requests: number }> {
  const located = await Promise.all(
    events.map(async event => {
      const records = (await call(
        "Get",
        {
          typeName: "LogRecord",
          search: { deviceSearch: { id: idOf(event.device) }, fromDate: event.activeFrom, toDate: event.activeFrom },
        },
        signal,
      )) as { longitude?: number; latitude?: number }[];
      const record = records?.[0];
      return record?.longitude != null && record.latitude != null
        ? { id: event.id, point: { x: record.longitude, y: record.latitude } }
        : undefined;
    }),
  );
  let requests = events.length;

  const withPoint = located.filter(item => !!item);
  const zones = new Map<string, string[]>();
  for (let start = 0; start < withPoint.length; start += addressBundle) {
    const bundle = withPoint.slice(start, start + addressBundle);
    const addresses = (await call(
      "GetAddresses",
      { coordinates: bundle.map(item => item.point), movingAddresses: false },
      signal,
    )) as { zones?: unknown[] }[];
    requests++;
    bundle.forEach((item, index) => {
      const ids = (addresses?.[index]?.zones ?? []).map(idOf).filter((id): id is string => !!id);
      zones.set(item.id, ids);
    });
  }
  return { zones, requests };
}
